import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getErrorMessage, logError } from '../../../utils/errorHandler';
import Modal from '../../Shared/Modal';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { loadBattlePrefs, saveBattlePrefs } from '../../../utils/battlePreferences';
import styles from './ContractBattles.module.css';

const SendChallenges = ({ displayToast }: { displayToast: (message: string, type: 'success' | 'error') => void }) => {
  const { mercenaryId } = useMercenary();
  interface Mercenary {
    id: number;
    name: string;
    level: number;
  }

  const [foundMercenaries, setFoundMercenaries] = useState<Mercenary[]>([]);
  const [minLevelSearch, setMinLevelSearch] = useState<number>(1);
  const [maxLevelSearch, setMaxLevelSearch] = useState<number>(50);
  const [showModal, setShowModal] = useState(false);
  const [selectedMercenaries, setSelectedMercenaries] = useState<number[]>([]); // For "Challenge All"
  const [strategy, setStrategy] = useState<string>('Normal');
  const [surrenderHp, setSurrenderHp] = useState<number>(0.5);

  useEffect(() => {
    if (!mercenaryId) return;
    const prefs = loadBattlePrefs(mercenaryId, 'contract-battle', {
      strategy: 'Normal',
      surrenderHP: 0.5,
      minLevel: 1,
      maxLevel: 50,
    });
    setStrategy((prefs.strategy as string) || 'Normal');
    setSurrenderHp((prefs.surrenderHP as number) ?? 0.5);
    setMinLevelSearch((prefs.minLevel as number) ?? 1);
    setMaxLevelSearch((prefs.maxLevel as number) ?? 50);
  }, [mercenaryId]);

  // Load preferences from cookies when opening the modal
  const loadPreferences = () => {
    if (!mercenaryId) return;
    const prefs = loadBattlePrefs(mercenaryId, 'contract-battle', {
      strategy: 'Normal',
      surrenderHP: 0.5,
      minLevel: minLevelSearch,
      maxLevel: maxLevelSearch,
    });
    setStrategy((prefs.strategy as string) || 'Normal');
    setSurrenderHp((prefs.surrenderHP as number) ?? 0.5);
  };

  const searchMercenaries = async () => {
    try {
      const response = await axios.get('/api/contract/search', {
        params: { levelMin: minLevelSearch, levelMax: maxLevelSearch, mercenaryId },
      });
      setFoundMercenaries(response.data);
    } catch (error) {
      logError(error, 'SendChallenges.searchMercenaries');
      displayToast(getErrorMessage(error, 'Failed to search for mercenaries'), 'error');
    }
  };

  const sendChallenge = async () => {
    try {
      await axios.post('/api/contract/challenge', {
        senderId: mercenaryId,
        receiverIds: selectedMercenaries,
        strategy,
        surrenderHp,
      });
      if (mercenaryId) {
        saveBattlePrefs(mercenaryId, 'contract-battle', {
          strategy,
          surrenderHP: surrenderHp,
          minLevel: minLevelSearch,
          maxLevel: maxLevelSearch,
        });
      }
      displayToast('Challenges sent successfully!', 'success');
      setShowModal(false);
      setFoundMercenaries((prev) =>
        prev.filter((merc) => !selectedMercenaries.includes(merc.id))
      );
    } catch (error) {
      displayToast('Failed to send challenges.', 'error');
    }
  };

  return (
    <div className={styles.sendChallenges}>
      {showModal && (
        <Modal
          show={showModal}
          onClose={() => setShowModal(false)}
          onConfirm={sendChallenge}
          title="Send Challenges"
          message={
            <div className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Combat Strategy</label>
                <select value={strategy} onChange={(e) => setStrategy(e.target.value)} className={styles.formControl}>
                  <optgroup label="Balanced">
                    <option value="Normal">Normal</option>
                    <option value="Normal: Light Attacks">Normal: Light Attacks</option>
                    <option value="Normal: Heavy Attacks">Normal: Heavy Attacks</option>
                  </optgroup>
                  <optgroup label="Aggressive">
                    <option value="Offensive">Offensive</option>
                    <option value="Offensive: Light Attacks">Offensive: Light Attacks</option>
                    <option value="Offensive: Heavy Attacks">Offensive: Heavy Attacks</option>
                  </optgroup>
                  <optgroup label="Cautious">
                    <option value="Defensive">Defensive</option>
                    <option value="Defensive: Light Attacks">Defensive: Light Attacks</option>
                    <option value="Defensive: Heavy Attacks">Defensive: Heavy Attacks</option>
                  </optgroup>
                  <optgroup label="Reckless">
                    <option value="Berserk">Berserk</option>
                    <option value="Berserk: Light Attacks">Berserk: Light Attacks</option>
                    <option value="Berserk: Heavy Attacks">Berserk: Heavy Attacks</option>
                  </optgroup>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Surrender Threshold: {surrenderHp * 100}% HP</label>
                <input 
                  type="range" 
                  min="0" 
                  max="0.9" 
                  step="0.1" 
                  value={surrenderHp} 
                  onChange={(e) => setSurrenderHp(parseFloat(e.target.value))} 
                  className={styles.rangeControl} 
                />
                <div className={styles.rangeLabels}>
                  <span>Death (0%)</span>
                  <span>Cautious (90%)</span>
                </div>
              </div>
            </div>
          }
        />
      )}

      <h3 className={styles.sectionTitle}>Search Mercenaries</h3>
      <div className={styles.filterSection}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Min Level:</label>
          <input
            type="number"
            value={minLevelSearch || ''}
            placeholder="1"
            onChange={(e) => setMinLevelSearch(e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
            className={styles.filterInput}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Max Level:</label>
          <input
            type="number"
            value={maxLevelSearch || ''}
            placeholder="50"
            onChange={(e) => setMaxLevelSearch(e.target.value === '' ? 0 : parseInt(e.target.value, 10) || 0)}
            className={styles.filterInput}
          />
        </div>
        <button onClick={searchMercenaries} className={styles.searchButton}>Search</button>
      </div>
      
      {foundMercenaries.length > 0 && (
        <button
          onClick={() => {
            setSelectedMercenaries(foundMercenaries.map((merc) => merc.id));
            loadPreferences();
            setShowModal(true);
          }}
          className={styles.challengeAllButton}
        >
          Challenge All
        </button>
      )}
      
      {foundMercenaries.length === 0 ? (
        <div className={styles.emptyState}>Search for mercenaries to challenge.</div>
      ) : (
        <ul className={styles.listContainer}>
          {foundMercenaries.map((merc) => (
            <li key={merc.id} className={styles.listItem}>
              <div className={styles.mercInfo}>
                <span className={styles.mercName}>{merc.name}</span>
                <span className={styles.mercLevel}>Lvl {merc.level}</span>
              </div>
              <div className={styles.buttonGroup}>
                <button
                  className={`${styles.actionButton} ${styles.challengeButton}`}
                  onClick={() => {
                    setSelectedMercenaries([merc.id]);
                    loadPreferences();
                    setShowModal(true);
                  }}
                >
                  Challenge
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SendChallenges;
